import React, { useState, useEffect } from 'react';
import { Grid, Box, CircularProgress, Typography } from '@mui/material';
import CostBarChart from './CostBarChart';
import costService from '../services/cost.service';
import { COST_CATEGORY_LABELS, COST_TYPE_LABELS } from '../types/cost.types';

// Interfaces para tipagem dos dados recebidos da API
interface CostAverageItem {
  name: string;
  averageCost: number;
  totalAmount?: number;
  count?: number;
}

interface DashboardData {
  costTypeAverage: Array<{name: string; value: number}>;
  technicianAverage: Array<{name: string; value: number}>;
  salesPersonAverage: Array<{name: string; value: number}>;
  categoryAverage: Array<{name: string; value: number}>;
}

const CostDashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    costTypeAverage: [],
    technicianAverage: [],
    salesPersonAverage: [],
    categoryAverage: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Buscar dados de custos médios diretamente das APIs específicas
      const [typeResponse, technicianResponse, salesPersonResponse, categoryResponse] = await Promise.all([
        costService.getCostAverageByType(),
        costService.getCostAverageByTechnician(),
        costService.getCostAverageBySalesPerson(),
        costService.getCostAverageByCategory()
      ]);
      
      console.log('Dados de custos médios por técnico:', technicianResponse);
      
      // Verificar se há dados disponíveis
      if (!typeResponse || !technicianResponse || !salesPersonResponse || !categoryResponse) {
        setDashboardData({
          costTypeAverage: [],
          technicianAverage: [],
          salesPersonAverage: [],
          categoryAverage: []
        });
        return;
      }
      
      // Formatar os dados recebidos diretamente das APIs
      const costTypeData: CostAverageItem[] = typeResponse.data || [];
      const technicianData: CostAverageItem[] = technicianResponse.data || [];
      const salesPersonData: CostAverageItem[] = salesPersonResponse.data || [];
      const categoryData: CostAverageItem[] = categoryResponse.data || [];
      
      console.log('Dados de vendedores recebidos:', salesPersonData);
      console.log('Quantidade de vendedores encontrados:', salesPersonData.length);
      
      // Forçar atualização do componente
      setDashboardData({
        costTypeAverage: costTypeData.map(item => ({
          name: COST_TYPE_LABELS[item.name as keyof typeof COST_TYPE_LABELS] || item.name,
          value: item.averageCost
        })),
        technicianAverage: technicianData
          .map(item => ({
            name: item.name,
            value: item.averageCost
          }))
          .slice(0, 3), // Top 3
        salesPersonAverage: salesPersonData
          .map(item => ({
            name: item.name,
            value: item.averageCost
          }))
          .slice(0, 3), // Top 3
        categoryAverage: categoryData
          .map(item => ({
            name: COST_CATEGORY_LABELS[item.name as keyof typeof COST_CATEGORY_LABELS] || item.name,
            value: item.averageCost
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 5) // Top 5
      });
    } catch (error: any) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setError(error.message || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Funções para processar os dados dos custos
  const processCostsByType = (costs: any[]) => {
    const typeGroups: Record<string, { total: number; count: number }> = {};
    
    costs.forEach(cost => {
      if (!cost.costType) return;
      
      if (!typeGroups[cost.costType]) {
        typeGroups[cost.costType] = { total: 0, count: 0 };
      }
      
      typeGroups[cost.costType].total += cost.amount || 0;
      typeGroups[cost.costType].count += 1;
    });
    
    // Calcular médias
    const result: Record<string, number> = {};
    Object.entries(typeGroups).forEach(([type, data]) => {
      result[type] = data.count > 0 ? data.total / data.count : 0;
    });
    
    return result;
  };
  
  const processCostsByTechnician = (costs: any[]) => {
    console.log('Processando custos por técnico...');
    console.log('Total de custos recebidos:', costs.length);
    
    const technicianGroups: Record<string, { total: number; count: number; name: string }> = {};
    let technicalDirectCosts = 0;
    let costsWithServiceRequest = 0;
    let costsWithAssignedTech = 0;
    
    costs.forEach(cost => {
      // Verificar se é um custo direto técnico
      if (cost.costType !== 'technical_direct') return;
      technicalDirectCosts++;
      
      // Verificar se tem um chamado associado
      if (!cost.serviceRequest) {
        console.log('Custo sem chamado associado:', cost._id);
        return;
      }
      costsWithServiceRequest++;
      
      // Obter o técnico responsável do chamado
      const serviceRequest = cost.serviceRequest;
      console.log('Chamado encontrado:', serviceRequest);
      
      if (!serviceRequest.assignedTo) {
        console.log('Chamado sem técnico responsável:', serviceRequest._id || serviceRequest);
        return;
      }
      costsWithAssignedTech++;
      
      // Extrair o ID e nome do técnico
      const techId = typeof serviceRequest.assignedTo === 'string' 
        ? serviceRequest.assignedTo 
        : serviceRequest.assignedTo._id;
        
      const techName = typeof serviceRequest.assignedTo === 'string' 
        ? 'Técnico' 
        : serviceRequest.assignedTo.displayName || serviceRequest.assignedTo.name || 'Técnico';
      
      console.log('Técnico encontrado:', techId, techName);
      
      if (!technicianGroups[techId]) {
        technicianGroups[techId] = { total: 0, count: 0, name: techName };
      }
      
      technicianGroups[techId].total += cost.amount || 0;
      technicianGroups[techId].count += 1;
    });
    
    console.log('Estatísticas de processamento:');
    console.log('- Custos técnicos diretos:', technicalDirectCosts);
    console.log('- Custos com chamado associado:', costsWithServiceRequest);
    console.log('- Custos com técnico responsável:', costsWithAssignedTech);
    console.log('- Grupos de técnicos encontrados:', Object.keys(technicianGroups).length);
    console.log('Grupos de técnicos:', technicianGroups);
    
    // Calcular médias e transformar em array
    const result = Object.values(technicianGroups)
      .map(data => ({
        name: data.name,
        averageCost: data.count > 0 ? data.total / data.count : 0
      }))
      .sort((a, b) => b.averageCost - a.averageCost); // Ordenar do maior para o menor
    
    console.log('Resultado final (técnicos):', result);
    return result;
  };
  
  const processCostsBySalesPerson = (costs: any[]) => {
    const salesPersonGroups: Record<string, { total: number; count: number; name: string }> = {};
    let totalCostsProcessed = 0;
    let costsWithVisit = 0;
    let costsWithSalesPerson = 0;
    
    costs.forEach(cost => {
      totalCostsProcessed++;
      
      // Verificar se o custo tem uma visita comercial associada
      if (!cost.commercialVisit) return;
      costsWithVisit++;
      
      // Verificar se a visita comercial tem um vendedor responsável
      if (!cost.commercialVisit.salesPerson) return;
      costsWithSalesPerson++;
      
      const commercialVisit = cost.commercialVisit;
      const salesPerson = commercialVisit.salesPerson;
      
      // Extrair ID e nome do vendedor
      const salesId = typeof salesPerson === 'string' ? salesPerson : salesPerson._id;
      const salesName = typeof salesPerson === 'string' ? 'Vendedor' : 
                      (salesPerson.displayName || salesPerson.name || 'Vendedor');
      
      // Inicializar grupo para este vendedor se ainda não existir
      if (!salesPersonGroups[salesId]) {
        salesPersonGroups[salesId] = { total: 0, count: 0, name: salesName };
      }
      
      // Acumular valor e contagem para cálculo da média
      salesPersonGroups[salesId].total += cost.amount || 0;
      salesPersonGroups[salesId].count += 1;
    });
    
    console.log(`Processamento de custos por vendedor no componente:\n - Total de custos: ${totalCostsProcessed}\n - Custos com visita comercial: ${costsWithVisit}\n - Custos com vendedor identificado: ${costsWithSalesPerson}`);
    console.log('Vendedores encontrados no componente:', Object.keys(salesPersonGroups).length);
    
    // Calcular médias e transformar em array
    return Object.values(salesPersonGroups)
      .map(data => ({
        name: data.name,
        averageCost: data.count > 0 ? data.total / data.count : 0
      }))
      .sort((a, b) => b.averageCost - a.averageCost); // Ordenar do maior para o menor
  };
  
  const processCostsByCategory = (costs: any[]) => {
    const categoryGroups: Record<string, { total: number; count: number }> = {};
    
    costs.forEach(cost => {
      if (!cost.category) return;
      
      if (!categoryGroups[cost.category]) {
        categoryGroups[cost.category] = { total: 0, count: 0 };
      }
      
      categoryGroups[cost.category].total += cost.amount || 0;
      categoryGroups[cost.category].count += 1;
    });
    
    // Calcular médias
    const result: Record<string, number> = {};
    Object.entries(categoryGroups).forEach(([category, data]) => {
      result[category] = data.count > 0 ? data.total / data.count : 0;
    });
    
    return result;
  };

  // Funções auxiliares para formatar os dados
  const formatCostTypeData = (data: Record<string, number>) => {
    return Object.entries(data).map(([key, value]) => ({
      name: COST_TYPE_LABELS[key as keyof typeof COST_TYPE_LABELS] || key,
      value: value
    }));
  };

  const formatPersonData = (data: Array<{name: string; averageCost: number}>) => {
    return data.map(item => ({
      name: item.name,
      value: item.averageCost
    }));
  };

  const formatCategoryData = (data: Record<string, number>) => {
    return Object.entries(data)
      .map(([key, value]) => ({
        name: COST_CATEGORY_LABELS[key as keyof typeof COST_CATEGORY_LABELS] || key,
        value: value
      }))
      .sort((a, b) => b.value - a.value); // Ordenar do maior para o menor
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" align="center">
          Erro ao carregar dados: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <CostBarChart 
          data={dashboardData.costTypeAverage}
          title="Custo médio por tipo de custo"
          color="#0088FE"
          height={275} // Altura aumentada em 10% (250 * 1.1 = 275)
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CostBarChart 
          data={dashboardData.technicianAverage}
          title="Custo médio por técnico (Top 3)"
          color="#00C49F"
          height={275} // Altura aumentada em 10%
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CostBarChart 
          data={dashboardData.salesPersonAverage}
          title="Custo médio por vendedor (Top 3)"
          color="#FFBB28"
          height={275} // Altura aumentada em 10%
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CostBarChart 
          data={dashboardData.categoryAverage}
          title="Custo médio por categoria (Top 5)"
          color="#FF8042"
          height={275} // Altura aumentada em 10%
        />
      </Grid>
    </Grid>
  );
};

export default CostDashboard;
